import { CommonFilterModel, CommonParams } from '@/api/resources/common/shared/models/common.models'
import { AgGridColumnType, AgGridExportType, ExportedColDef, ExportedColGroupDef } from '@/shared/models/ag-grid.model'
import i18n from '@/lang/i18n'
import { ColDef } from '@ag-grid-community/core'
import { StatisticalAnalysisDatasource } from '@/modules/statistical-analysis-module/handlers/statistical-analysis-datasource'
import { StatisticalAnalysisViewDetailTableFilters } from '@/modules/statistical-analysis-module/shared/components/statistical-analysis-view-detail-table/statistical-analysis-view-detail-table.model'
import { Nullable } from '@/shared/types/app.types'
import { InfinitePaginatorResponse } from '@/shared/resources/infinite-datasources/infinite-resource.model'
import { StatisticalAnalysisSampleTagsStatisticsDossierBaseRequest, StatisticalAnalysisSampleTagsStatisticsDossierDetailRequest, StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTable, StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTableContentCase, StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum } from '@/modules/statistical-analysis-module/services/sample-tags-dossier/sample-tags-dossier.dto'
import { sampleTagsStatisticsDossierService } from '@/modules/statistical-analysis-module/services/sample-tags-dossier/sample-tags-dossier.service'
import { StatisticalAnalysisSampleTagsDossierExternalInfo } from '@/modules/statistical-analysis-module/views/labeled-samples-dossier/labeled-samples-dossier.model'
import { ColGroupDef } from '@ag-grid-enterprise/all-modules'
import { concat, forEach } from 'lodash'
import { StatisticalAnalysisGroupingResponseField } from '@/modules/statistical-analysis-module/shared/services/statistical-analysis-grouping/statistical-analysis-grouping.dto'

export class StatisticalAnalysisHandlerDatasourceSampleTagsDossier extends StatisticalAnalysisDatasource<StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTableContentCase> {
    protected statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>

    constructor(statisticalAnalysisFilters: Nullable<StatisticalAnalysisViewDetailTableFilters>) {
        super()
        this.statisticalAnalysisFilters = statisticalAnalysisFilters
    }

    public getGridColumns(): ColDef[] | ColGroupDef[] | ExportedColGroupDef[] | ExportedColDef[] {
        return concat([], [{ type: AgGridColumnType.CUSTOM_CHECKBOX } as ColDef], this._filterGroupedGridColumns(), this._getInfoColumns())
    }

    public async paginator(filterModel: CommonFilterModel, params: CommonParams, scrollId: string): Promise<InfinitePaginatorResponse<StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTableContentCase>> {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisSampleTagsDossierExternalInfo
        const baseRequest: StatisticalAnalysisSampleTagsStatisticsDossierBaseRequest = sampleTagsStatisticsDossierService.getBaseRequest(sidebarFilter)
        const detailRequest: StatisticalAnalysisSampleTagsStatisticsDossierDetailRequest = {
            groupByFields: (this.statisticalAnalysisFilters?.grouping as StatisticalAnalysisGroupingResponseField[]).map((item: StatisticalAnalysisGroupingResponseField) => item.fieldName)
        }
        const sampleTagId = sidebarFilter.trackingGeneralSelected?.key as number

        const response: StatisticalAnalysisSampleTagsStatisticsDossierDetailViewTable = await sampleTagsStatisticsDossierService.getDetailViewTable(filterModel, params, sampleTagId, baseRequest, detailRequest, scrollId)
        return {
            content: response?.content,
            scrollPage: {
                scrollId: encodeURIComponent(JSON.stringify(response?.scrollPage?.scrollId)),
                size: response?.scrollPage?.size,
                totalElements: null
            }
        }
    }

    private _getInfoColumns(): ExportedColGroupDef[] {
        const sidebarFilter = this.statisticalAnalysisFilters?.externalFiltering.sidebarFilters as StatisticalAnalysisSampleTagsDossierExternalInfo
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.statistics'),
            children: [
                {
                    type: AgGridColumnType.NUMBER,
                    headerName: i18n.tc(sidebarFilter?.groupByCase ? 'statisticalAnalysis.charts.totalCases' : 'statisticalAnalysis.charts.totalSamples'),
                    field: sidebarFilter?.groupByCase ? 'totalCases' : 'totalSamples',
                    exportType: AgGridExportType.NUMBER,
                    minWidth: 200,
                    flex: 1,
                    suppressSizeToFit: true,
                    filter: false,
                    sortable: false
                }
            ]
        }]
    }

    private _getGroupingColumns(): ExportedColDef[] {
        return [
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.caseType'),
                field: 'caseType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.sampleType'),
                field: 'sampleType.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.sampleTag'),
                field: 'sampleTag.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.priority'),
                field: 'priority.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingDepartment'),
                field: 'physicianDepartment.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingCenter'),
                field: 'physicianCenter.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingHealthManagementArea'),
                field: 'physicianHealthManagementArea.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 200,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.regime'),
                field: 'regime.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.procedure'),
                field: 'procedure.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.mostLikelyDiagnose'),
                field: 'mostLikelyMorphologicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.topographicCode'),
                field: 'topographicCode.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.pathologist'),
                field: 'pathologist.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.TEXT,
                headerName: i18n.tc('statisticalAnalysis.grids.requestingProfessional'),
                field: 'physicianPetitioner.literal',
                exportType: AgGridExportType.TEXT,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            },
            {
                type: AgGridColumnType.CHECK_ICON,
                headerName: i18n.tc('statisticalAnalysis.grids.consultationCase'),
                field: 'caseOfReference.literal',
                exportType: AgGridExportType.BOOLEAN,
                minWidth: 100,
                flex: 1,
                suppressSizeToFit: true,
                filter: false,
                sortable: false
            }
        ]
    }

    private _filterGroupedGridColumns(): ColGroupDef[] {
        const colDefs: ColDef[] = []
        const groupedColumns = this._getGroupingColumns()
        forEach(this.statisticalAnalysisFilters?.grouping, (item: StatisticalAnalysisGroupingResponseField) => {
            switch (item.fieldName as StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum) {
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.CASE_TYPE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'caseType.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.REGIME:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'regime.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PRIORITY:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'priority.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PATHOLOGIST:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'pathologist.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.SAMPLE_TYPE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'sampleType.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.CASE_OF_REFERENCE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'caseOfReference.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PHYSICIAN_HEALTH_MANAGEMENT_AREA:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianHealthManagementArea.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PHYSICIAN_CENTER:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianCenter.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PHYSICIAN_DEPARTMENT:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianDepartment.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PHYSICIAN_PETITIONER:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'physicianPetitioner.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.TOPOGRAPHIC_CODE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'topographicCode.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.PROCEDURE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'procedure.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.MOST_LIKELY_MORPHOLOGIC_CODE:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'mostLikelyMorphologicCode.literal') as ColDef)
                break
            case StatisticalAnalysisSampleTagsStatisticsDossierGroupColumnsEnum.SAMPLE_TAG:
                colDefs.push(groupedColumns.find((item: ColDef) => item.field === 'sampleTag.literal') as ColDef)
                break
            default: break
            }
        })
        return [{
            headerName: i18n.tc('statisticalAnalysis.grids.groupings'),
            children: colDefs
        }]
    }
}
